import React from "react";
import { Formik } from "formik";
import { navigate } from "@reach/router";
import { useSnackbar } from "notistack";
import FormField from "../../../common/FormField/FormField";
import { CustomCheckbox, CustomFileUpload } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import { splitPageNumbersValidationSchema } from "../../../../helpers/validationSchema";
import { StyledForm, ButtonGroup, BackButton, SubmitButton } from "./styles";
import { Card, FormGrid, Label, PageWrapper, Section } from "./SplitAndUpload";

const SplitPageNumbers = () => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, setFieldValue) {
    if (!file) return;

    try {
      if (file.type !== "application/pdf") throw new Error("Invalid File Type");
      setLoader({ state: true, message: `Uploading file ${file.name} ...` });
      const s3Response = await AWSService.getS3URL({ key: file.name });
      if (s3Response.url) {
        const fileUrl = await AWSService.uploadToS3(s3Response.url, file);
        setFieldValue("fileUrl", fileUrl);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Processing split..." });
      const response = await CaseService.splitPDFWithPages({
        pageNumber: values.pageNumber,
        fileUrl: values.fileUrl,
        splitSeparately: values.splitSeparately,
      });
      if (response?.fileUrl) {
        const downloadLink = document.createElement("a");
        downloadLink.href = response.fileUrl;
        downloadLink.download = getFileName(response.fileUrl);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        enqueueSnackbar("File split successfully", { variant: "success" });
        resetForm();
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{ pageNumber: "", fileUrl: "", splitSeparately: true }}
          validationSchema={splitPageNumbersValidationSchema}
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
          }) => (
            <StyledForm onSubmit={handleSubmit}>
              <Section>
                <FormGrid>
                  <FormField
                    input={{
                      name: "pageNumber",
                      type: "text",
                      label: "Enter Page Numbers",
                      required: true,
                      placeholder: "1,101-10,5-101-10,8,1-10",
                    }}
                    values={values}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    touched={touched}
                    errors={errors}
                  />
                  <div>
                    <Label>
                      Upload File <span className="required-star"> *</span>
                    </Label>
                    <CustomFileUpload
                      error={touched.fileUrl && errors.fileUrl}
                      setFile={(file) => s3Upload(file, setFieldValue)}
                      style={{ width: "100%", boxSizing: "border-box" }}
                      accept=".pdf"
                      file={{ name: getFileName(values.fileUrl) }}
                    />
                  </div>
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <CustomCheckbox
                      checked={values.splitSeparately}
                      onChange={() =>
                        setFieldValue(
                          "splitSeparately",
                          !values.splitSeparately,
                        )
                      }
                      name="splitSeparately"
                    />
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Label style={{ margin: 0 }}>
                        Split into separate pages
                      </Label>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "3px 10px",
                          borderRadius: 100,
                          background: values.splitSeparately
                            ? "#d1fae5"
                            : "#f1f5f9",
                          color: values.splitSeparately ? "#059669" : "#94a3b8",
                          letterSpacing: 0.5,
                        }}
                      >
                        {values.splitSeparately ? "ON" : "OFF"}
                      </span>
                    </div>
                  </div>
                </FormGrid>
              </Section>
              <ButtonGroup>
                <BackButton
                  type="button"
                  onClick={() => navigate("/dashboard/case-manager")}
                >
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default SplitPageNumbers;
